import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { I18nProvider } from "@/contexts/I18nContext";
import { PaywallProvider } from "@/contexts/PaywallContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import AuthCallback from "@/components/AuthCallback";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Numerology from "@/pages/Numerology";
import NameNumerology from "@/pages/NameNumerology";
import LoShuGrid from "@/pages/LoShuGrid";
import BirthChart from "@/pages/BirthChart";
import Panchang from "@/pages/Panchang";
import AstroChat from "@/pages/AstroChat";
import Matching from "@/pages/Matching";
import Profile from "@/pages/Profile";
import PalmReading from "@/pages/PalmReading";
import Transits from "@/pages/Transits";
import Muhurat from "@/pages/Muhurat";
import Vastu from "@/pages/Vastu";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import About from "@/pages/About";
import Learn from "@/pages/Learn";
import Article from "@/pages/Article";

function AppRouter() {
  const location = useLocation();
  // Reset scroll position on every route change — needed for Prev/Next article
  // navigation to open the new page from the top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  // Synchronous check: if returning from Google OAuth, render callback first
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout><Dashboard /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/numerology"
        element={
          <ProtectedRoute>
            <Layout><Numerology /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/name-numerology"
        element={
          <ProtectedRoute>
            <Layout><NameNumerology /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lo-shu-grid"
        element={
          <ProtectedRoute>
            <Layout><LoShuGrid /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/birth-chart"
        element={
          <ProtectedRoute>
            <Layout><BirthChart /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/panchang"
        element={
          <ProtectedRoute>
            <Layout><Panchang /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <Layout><AstroChat /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/matching"
        element={
          <ProtectedRoute>
            <Layout><Matching /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout><Profile /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/palm-reading"
        element={
          <ProtectedRoute>
            <Layout><PalmReading /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/transits"
        element={
          <ProtectedRoute>
            <Layout><Transits /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/muhurat"
        element={
          <ProtectedRoute>
            <Layout><Muhurat /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vastu"
        element={
          <ProtectedRoute>
            <Layout><Vastu /></Layout>
          </ProtectedRoute>
        }
      />

      <Route path="/privacy" element={<Privacy />} />
      <Route path="/privacy-policy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/terms-of-service" element={<Terms />} />
      <Route path="/about" element={<About />} />
      <Route path="/learn" element={<Learn />} />
      <Route path="/learn/:slug" element={<Article />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => { /* ignore */ });
    }
    // Set the default tab title ONLY if a child page hasn't already set its own
    // title. Child useEffects fire before parent useEffects on mount, so by the
    // time this runs an Article/Learn page may already have set a per-page
    // title — we must not overwrite it.
    const DEFAULT_TITLE = "Cosomic Sage — Vedic Astrology, Numerology & AI Astro Talk";
    const looksDefault = () => {
      const t = (document.title || "").trim();
      return !t || t === "React App" || t === "Cosomic Sage";
    };
    if (looksDefault()) document.title = DEFAULT_TITLE;
    // Guard against external scripts resetting the title back to "React App" —
    // but preserve any legitimate per-page title the current page has set.
    const enforce = () => {
      if (looksDefault()) document.title = DEFAULT_TITLE;
    };
    const id = window.setInterval(enforce, 1500);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <I18nProvider>
          <AuthProvider>
            <PaywallProvider>
              <AppRouter />
              <Toaster theme="dark" />
            </PaywallProvider>
          </AuthProvider>
        </I18nProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
